import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useClaudeTasksByIds,
  useCompanyRequests,
  useProfiles,
  type ClaudeTaskSummary,
  type CompanyRequest,
  type ProfileSummary,
} from '@/hooks/useCompanyRequests';
import { useTeams, type TeamSummary } from '@/hooks/useTeams';

const HIDDEN_TEAM_NAMES = new Set(['PricingSaaS', "john's Team"]);

function profileLabel(p?: ProfileSummary): string {
  if (!p) return '';
  if (p.display_name) return p.display_name;
  const composed = [p.first_name, p.last_name].filter(Boolean).join(' ');
  if (composed) return composed;
  return p.email ?? '';
}

// Explicit color map so success/failure/running/pending are visually distinct
// (default/secondary/destructive variants alone all read as muted greys + red).
const STATUS_COLORS: Record<string, string> = {
  completed: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-100',
  success: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-100',
  failed: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-100',
  failure: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-100',
  running: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100',
  pending: 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100',
  archived: 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-100',
};

function colorFor(status: string): string {
  return STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-700 border-gray-200';
}

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge variant="outline" className={colorFor(status)}>
      {status}
    </Badge>
  );
}

// Map claude_tasks.status raw values to a friendly label.
// "completed"/"failed" come from the worker; "running" only briefly during execution.
function taskStatusLabel(status: string): string {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'failure';
    case 'running':
      return 'running';
    default:
      return status;
  }
}

function TaskStatusBadge({ status }: { status: string }) {
  return (
    <Badge variant="outline" className={colorFor(status)}>
      {taskStatusLabel(status)}
    </Badge>
  );
}

function TaskCell({ taskId, task }: { taskId: string; task?: ClaudeTaskSummary }) {
  return (
    <div className="flex flex-col gap-0.5">
      <Link
        to={`/tasks/${taskId}`}
        className="font-mono text-xs text-blue-600 hover:underline"
        title={taskId}
      >
        {taskId.slice(0, 8)}
      </Link>
      {task ? (
        <TaskStatusBadge status={task.status} />
      ) : (
        <span className="text-xs text-gray-400">…</span>
      )}
    </div>
  );
}

export default function Requests() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState<string>('default');

  const { data: requests, isLoading, error } = useCompanyRequests({
    status: statusFilter === 'all' ? undefined : statusFilter,
    limit: 500,
  });

  const filteredBySlug = useMemo(() => {
    const base = requests ?? [];
    if (!search.trim()) return base;
    const q = search.toLowerCase();
    return base.filter(
      (r) =>
        r.slug.toLowerCase().includes(q) ||
        (r.canonical_slug ?? '').toLowerCase().includes(q) ||
        r.pricing_page_url.toLowerCase().includes(q)
    );
  }, [requests, search]);

  // Team + requester lookups
  const teamIds = useMemo(
    () => filteredBySlug.map((r) => r.team_id).filter((id): id is string => !!id),
    [filteredBySlug]
  );
  const userIds = useMemo(
    () => filteredBySlug.map((r) => r.requested_by),
    [filteredBySlug]
  );
  const taskIds = useMemo(
    () => filteredBySlug.map((r) => r.task_id).filter((id): id is string => !!id),
    [filteredBySlug]
  );
  const { data: teamMap, isLoading: teamsLoading } = useTeams(teamIds);
  const { data: profileMap, isLoading: profilesLoading } = useProfiles(userIds);
  const { data: taskMap } = useClaudeTasksByIds(taskIds);

  const visibleRequests = useMemo<CompanyRequest[]>(() => {
    if (teamFilter === 'all') return filteredBySlug;
    if (!teamMap) return [];
    if (teamFilter === 'default') {
      return filteredBySlug.filter((r) => {
        if (!r.team_id) return false;
        const t = teamMap.get(r.team_id);
        if (!t) return false;
        return !HIDDEN_TEAM_NAMES.has(t.name);
      });
    }
    return filteredBySlug.filter((r) => r.team_id === teamFilter);
  }, [filteredBySlug, teamMap, teamFilter]);

  const teamOptions = useMemo<TeamSummary[]>(() => {
    if (!teamMap) return [];
    return Array.from(teamMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [teamMap]);

  const counts = useMemo(() => {
    const c = { pending: 0, completed: 0, failed: 0, other: 0 };
    for (const r of requests ?? []) {
      if (r.status === 'pending') c.pending++;
      else if (r.status === 'completed') c.completed++;
      else if (r.status === 'failed') c.failed++;
      else c.other++;
    }
    return c;
  }, [requests]);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Company / page add requests</CardTitle>
            <div className="text-xs text-gray-500">
              {counts.pending} pending · {counts.completed} completed · {counts.failed} failed
            </div>
          </div>
          <p className="text-sm text-gray-500">
            User-submitted requests to add a new company or pricing page, with status
            from the backend processing task.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Input
              className="max-w-xs"
              placeholder="Search slug or URL…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={teamFilter} onValueChange={setTeamFilter}>
              <SelectTrigger className="w-[240px]">
                <SelectValue placeholder="Team filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Customer teams (default)</SelectItem>
                <SelectItem value="all">All teams (incl. internal)</SelectItem>
                {teamOptions.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && (
            <div className="text-sm text-red-600">{(error as Error).message}</div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Requested</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Team</TableHead>
                <TableHead>Requester</TableHead>
                <TableHead>Completed</TableHead>
                <TableHead>Task</TableHead>
                <TableHead>Failure</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(isLoading || teamsLoading || profilesLoading) && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-gray-500">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                !teamsLoading &&
                !profilesLoading &&
                visibleRequests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-gray-500">
                      {(requests ?? []).length === 0
                        ? 'No requests yet.'
                        : 'No requests match these filters.'}
                    </TableCell>
                  </TableRow>
                )}
              {visibleRequests.map((r) => {
                const team = r.team_id ? teamMap?.get(r.team_id) : undefined;
                const profile = profileMap?.get(r.requested_by);
                return (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs whitespace-nowrap">
                      {format(new Date(r.requested_at), 'yyyy-MM-dd HH:mm')}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">{r.slug}</span>
                        {r.canonical_slug && r.canonical_slug !== r.slug && (
                          <span className="text-xs text-gray-500">
                            → {r.canonical_slug}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs max-w-xs truncate">
                      <a
                        href={r.pricing_page_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline inline-flex items-center gap-1"
                      >
                        <span className="truncate">{r.pricing_page_url}</span>
                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      </a>
                    </TableCell>
                    <TableCell>
                      {team ? (
                        <div className="flex flex-col">
                          <span className="text-sm">{team.name}</span>
                          <span className="text-xs text-gray-400">{team.slug}</span>
                        </div>
                      ) : r.team_id ? (
                        <span
                          className="font-mono text-xs text-gray-400"
                          title={r.team_id}
                        >
                          {r.team_id.slice(0, 8)}…
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {profile ? (
                        <div className="flex flex-col">
                          <span className="text-sm">{profileLabel(profile)}</span>
                          {profile.email &&
                            profile.email !== profileLabel(profile) && (
                              <span className="text-xs text-gray-400">
                                {profile.email}
                              </span>
                            )}
                        </div>
                      ) : (
                        <span
                          className="font-mono text-xs text-gray-400"
                          title={r.requested_by}
                        >
                          {r.requested_by.slice(0, 8)}…
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {r.completed_at
                        ? format(new Date(r.completed_at), 'yyyy-MM-dd HH:mm')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {r.task_id ? (
                        <TaskCell taskId={r.task_id} task={taskMap?.get(r.task_id)} />
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-gray-600 max-w-xs truncate">
                      {r.failure_reason ? (
                        <span title={r.failure_message ?? ''}>
                          <span className="font-medium">{r.failure_reason}</span>
                          {r.failure_message && `: ${r.failure_message}`}
                        </span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
